import {Component, EventEmitter, Input, OnInit, OnDestroy, Output} from '@angular/core';
import {forkJoin, of, Subscription} from 'rxjs';
import {EnvService} from "../services/env.service";
import {HttpClient} from "@angular/common/http";
import { ActivatedRoute, Router, Params } from '@angular/router';
import {AlertService} from "../services/alert.service";
import {AuthService} from "../services/auth.service";
import { MatTableDataSource } from '@angular/material/table';

@Component({
  selector: 'micro-ne-detail',
  templateUrl: './ne-detail.component.html'
})
export class NeDetailComponent implements OnInit, OnDestroy {
  dataSource = new MatTableDataSource<any>();
  @Input()
  neId:any;

  ne:any;
  validatingCompany = false;
  companyValidationFailed = false;
  private companyValidation?: Subscription;
  private loadRequest?: Subscription;
  private subscriptions = new Subscription();
  private readonly companyFields = [
    {id: 'netId', endpoint: 'nets'},
    {id: 'neTypeId', endpoint: 'neTypes', selected: 'selectedNeType', name: 'neTypeName'},
    {id: 'classCodeId', endpoint: 'cmdbClassCodes', selected: 'selectedClassCode', name: 'classCodeName'},
    {id: 'hostingId', endpoint: 'cmdbHostingPlatform', selected: 'selectedHostingPlatform', name: 'hostingPlatformName'},
    {id: 'clusterId', endpoint: 'cmdbClusters', selected: 'selectedCluster'},
    {id: 'locationId', endpoint: 'locations'},
    {id: 'osVersionId', endpoint: 'cmdbOsVersions', selected: 'selectedOsVersion', name: 'osVersionName'},
    {id: 'applicationTagId', endpoint: 'cmdbApplicationTags', selected: 'selectedApplicationTag', name: 'applicationTagName'},
    {id: 'environmentId', endpoint: 'environments'}
  ];
  
  selectedClassCode: any = '';
  classCodeName: any = '';

  selectedOsVersion: any = '';
  osVersionName: any = '';

  selectedApplicationTag: any = '';
  applicationTagName: any = '';

  selectedHostingPlatform: any = '';
  hostingPlatformName: any = '';

  selectedCluster: any;
  
  selectedNeType: any = '';
  neTypeName: any = '';

  @Output()
  persisted:EventEmitter<any> = new EventEmitter<any>();

  constructor(protected env: EnvService,
              protected http: HttpClient,
              private route: ActivatedRoute,
              private router:Router,
              protected authService:AuthService,
              private alertService:AlertService) {
  }

  public ngOnInit(): void {
    if (this.neId) {
      this.load(this.neId);
    } else {
      this.subscriptions.add(this.route.params
        .subscribe((params: Params) => {
          let id:any = params['id'];
          if (id === '0') {
            this.createNew();
          } else {
            this.load(id);
          }
        }));
    }
  }

  load(id:any) {
    this.loadRequest?.unsubscribe();
    this.companyValidation?.unsubscribe();
    this.resetCompanySelections();
    this.validatingCompany = true;
    this.loadRequest = this.http.get(`${this.env.e.url}/domain/nes/${id}`).subscribe(
      data => {
        this.ne = data as any;
        this.validateCompanySelections();
      }
    );
  }

  createNew() {
    this.loadRequest?.unsubscribe();
    this.companyValidation?.unsubscribe();
    this.resetCompanySelections();
    this.ne = {
      companyId: this.authService.getCompanyId(),
      clusterId: null,
      negs: [],
      sites: []
    };
  }

  save() {
    if (this.validatingCompany || this.companyValidationFailed || !this.ne.companyId) {
      return;
    }
    if (!this.ne.id) {
      this.ne.neName = this.ne.name;

      if (!this.authService.isCompanyAdmin()) this.ne.companyId = this.authService.getCompanyId();

      this.http.post(`${this.env.e.url}/domain/nes`, this.ne)
        .subscribe(
          data => {
            this.ne = data;
            this.alertService.info(`Created ${this.ne.name}`);
            this.persisted.emit({
              data: data,
              action: 'added'
            });
          }
        );

    } else {
      this.ne.neName = this.ne.name;
      this.http.put(`${this.env.e.url}/domain/nes/${this.ne.id}`, this.ne)
        .subscribe(
          data => {
            this.ne = data;
            this.alertService.info(`Updated Network Element Settings`);
            this.persisted.emit({
              data: data,
              action: 'updated'
            });
          }
        );
    }
  }

  delete() {
    this.http.delete(`${this.env.e.url}/domain/nes/${this.ne.id}`)
      .subscribe(
        complete => {
          this.persisted.emit({
            data: this.ne,
            action: 'deleted'
          });
          this.alertService.info(`Deleted Network Element ${this.ne.name}`);
          if (!this.neId) {
            this.router.navigate(["/nes"]);
          }
        }
      );
  }

  clearApplicationTag() {
    this.ne.applicationTagId = null;
    this.selectedApplicationTag = undefined;
    this.applicationTagName = null;
  }

  onCompanyChange(companyId: any): void {
    if (companyId === this.ne.companyId) return;
    this.ne.companyId = companyId;
    this.validateCompanySelections();
  }

  validateCompanySelections(): void {
    this.companyValidation?.unsubscribe();
    this.companyValidationFailed = false;
    this.validatingCompany = false;
    const ne = this.ne;
    const companyId = ne.companyId;
    if (!companyId) return;

    // Read selected records directly; a filtered list or dropdown page cannot
    // establish the ownership of a selection that is absent from its results.
    const fields = this.companyFields.map(field => ({...field, value: ne[field.id]}));
    const picklists = [
      {id: 'negs', endpoint: 'objectGroups', values: [...(ne.negs || [])]},
      {id: 'sites', endpoint: 'sites', values: [...(ne.sites || [])]}
    ];
    const requests = fields.map(field => field.value
      ? this.http.get<any>(`${this.env.e.url}/domain/${field.endpoint}/${field.value}`)
      : of(null));
    const groupRequests = picklists.map(list => list.values.length
      ? forkJoin(list.values.map(id => this.http.get<any>(`${this.env.e.url}/domain/${list.endpoint}/${id}`)))
      : of([] as any[]));

    this.validatingCompany = true;
    this.companyValidation = forkJoin({
      records: forkJoin(requests), groups: forkJoin(groupRequests)
    }).subscribe({
      next: ({records, groups}) => {
        if (this.ne !== ne || ne.companyId !== companyId) return;
        let unresolved = false;
        fields.forEach((field, index) => {
          const record = records[index];
          const knownCompany = record?.companyId;
          if (field.value && !knownCompany) unresolved = true;
          if (knownCompany && knownCompany !== companyId) ne[field.id] = null;
          const selected = ne[field.id] ? record : undefined;
          if (field.selected) this[field.selected] = selected;
          if (field.name) this[field.name] = selected?.name || '';
        });
        picklists.forEach((list, index) => {
          ne[list.id] = list.values.filter((id, itemIndex) => {
            const record = groups[index][itemIndex];
            if (!record?.companyId) {
              unresolved = true;
              return true;
            }
            return record.companyId === companyId;
          });
        });
        this.companyValidationFailed = unresolved;
        this.validatingCompany = false;
      },
      error: () => {
        this.validatingCompany = false;
        this.companyValidationFailed = true;
        this.alertService.warn('Unable to validate the selected records for this company. Please retry.');
      }
    });
  }

  private resetCompanySelections(): void {
    this.validatingCompany = false;
    this.companyValidationFailed = false;
    this.companyFields.forEach(field => {
      if (field.selected) this[field.selected] = undefined;
      if (field.name) this[field.name] = '';
    });
  }

  ngOnDestroy(): void {
    this.companyValidation?.unsubscribe();
    this.loadRequest?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  onClassCodeChange(classCode: any): void {
    this.selectedClassCode = classCode;
    this.classCodeName = classCode ? classCode.name : '';
  }

  onOsVersionChange(osVersion: any): void {
    this.selectedOsVersion = osVersion;
    this.osVersionName = osVersion ? osVersion.name : '';
  }

  onApplicationTagChange(applicationTag: any): void {
    this.selectedApplicationTag = applicationTag;
    this.applicationTagName = applicationTag ? applicationTag.name : '';
  }

  onHostingPlatformChange(hostingPlatform: any): void {
    this.selectedHostingPlatform = hostingPlatform;
    this.hostingPlatformName = hostingPlatform ? hostingPlatform.name : '';
  }

  onNeTypeChange(neType: any): void {
    this.selectedNeType = neType;
    this.neTypeName = neType ? neType.name : '';
  }
}
